import { Checkbox, Form, Input, Modal, Select, Button, Space, message, Tooltip } from "antd";
import { PlusOutlined, MinusCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  useAddDebtorAssetCustomFieldMutation,
  useUpdateDebtorAssetCustomFieldMutation,
} from "features/userDefinedFields/debtorAssetCustomFieldsAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import {
  useAddAccountUdfCustomFieldMutation,
  useUpdateAccountUdfCustomFieldMutation,
} from "features/userDefinedFields/agencyPortal/accountUserDefinedFieldsAPI";
import {
  useAddCreditorUdfCustomFieldMutation,
  useUpdateCreditorUdfCustomFieldMutation,
} from "features/userDefinedFields/agencyPortal/creditorUserDefinedFieldsAPI";
import {
  useAddDebtorUdfCustomFieldMutation,
  useUpdateDebtorUdfCustomFieldMutation,
} from "features/userDefinedFields/agencyPortal/debtorUserDefinedFieldsAPI";
import {
  useAddLegalCustomFieldMutation,
  useUpdateLegalCustomFieldMutation,
} from "features/userDefinedFields/legalCustomFieldsAPI";
import styled from "styled-components";
import {
  useAddUserUdfCustomFieldMutation,
  useUpdateUserUdfCustomFieldMutation,
} from "features/userDefinedFields/agencyPortal/userUserDefinedFieldsAPI";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 350px;
`;

const StyledSpace = styled(Space)`
  display: flex;
  margin-bottom: 8px;
  width: 100%;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 0;
  display: flex;
`;

const StyledInfoCircleOutlined = styled(InfoCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

export default function AddUserDefinedFieldModal({
  onSubmit,
  userDefinedField = undefined,
  onCancel,
  forType,
}) {
  const [form] = Form.useForm();
  const { data: constants } = useFetchBackendConstantsQuery();
  const isNew = !userDefinedField;
  const selectedType = Form.useWatch("type", form);

  // API calls hooks
  const [addAccountUdfCustomField, { isLoading: isAddAccountUdfLoading }] =
    useAddAccountUdfCustomFieldMutation();
  const [updateAccountUdfCustomField, { isLoading: isUpdateAccountUdfLoading }] =
    useUpdateAccountUdfCustomFieldMutation();
  const [addCreditorUdfCustomField, { isLoading: isAddCreditorUdfLoading }] =
    useAddCreditorUdfCustomFieldMutation();
  const [updateCreditorUdfCustomField, { isLoading: isUpdateCreditorUdfLoading }] =
    useUpdateCreditorUdfCustomFieldMutation();
  const [addDebtorUdfCustomField, { isLoading: isAddDebtorUdfLoading }] =
    useAddDebtorUdfCustomFieldMutation();
  const [updateDebtorUdfCustomField, { isLoading: isUpdateDebtorUdfLoading }] =
    useUpdateDebtorUdfCustomFieldMutation();
  const [updateLegalCustomField, { isLoading: isUpdateLegalLoading }] =
    useUpdateLegalCustomFieldMutation();
  const [addCustomLegalField, { isLoading: isAddLegalLoading }] = useAddLegalCustomFieldMutation();
  const [addUserUdfCustomField, { isLoading: isAddUserUdfLoading }] =
    useAddUserUdfCustomFieldMutation();
  const [updateUserUdfCustomField, { isLoading: isUpdateUserUdfLoading }] =
    useUpdateUserUdfCustomFieldMutation();
  const [addDebtorAssetCustomField, { isLoading: isAddDebtorAssetLoading }] =
    useAddDebtorAssetCustomFieldMutation();
  const [updateDebtorAssetCustomField, { isLoading: isUpdateDebtorAssetLoading }] =
    useUpdateDebtorAssetCustomFieldMutation();

  const isLoading =
    isAddAccountUdfLoading ||
    isUpdateAccountUdfLoading ||
    isAddDebtorUdfLoading ||
    isUpdateDebtorUdfLoading ||
    isUpdateLegalLoading ||
    isAddLegalLoading ||
    isAddUserUdfLoading ||
    isUpdateUserUdfLoading ||
    isAddDebtorAssetLoading ||
    isUpdateDebtorAssetLoading;

  const createAction = {
    account: addAccountUdfCustomField,
    client: addCreditorUdfCustomField,
    debtor: addDebtorUdfCustomField,
    debtorAsset: addDebtorAssetCustomField,
    legal: addCustomLegalField,
    user: addUserUdfCustomField,
  }[forType];

  const updateAction = {
    account: updateAccountUdfCustomField,
    client: updateCreditorUdfCustomField,
    debtor: updateDebtorUdfCustomField,
    debtorAsset: updateDebtorAssetCustomField,
    legal: updateLegalCustomField,
    user: updateUserUdfCustomField,
  }[forType];

  const onOk = async () => {
    const fields = await form.validateFields();
    const action = isNew ? createAction : updateAction;
    const result = await action(fields);
    if ("data" in result) {
      const successText = {
        account: `User defined field ${isNew ? "created" : "updated"} successfully!`,
        client: `User defined field ${isNew ? "created" : "updated"} successfully!`,
        debtor: `User defined field ${isNew ? "created" : "updated"} successfully!`,
        debtorAsset: `Debtor asset custom field ${isNew ? "created" : "updated"} successfully!`,
        legal: `Legal custom field ${isNew ? "created" : "updated"} successfully!`,
        user: `User defined field ${isNew ? "created" : "updated"} successfully!`,
      }[forType];
      message.success(successText);
      await onSubmit(fields);
    }

    if ("error" in result) {
      const errorText = {
        account: `Failed to ${isNew ? "create" : "update"} user defined field`,
        client: `Failed to ${isNew ? "create" : "update"} user defined field`,
        debtor: `Failed to ${isNew ? "create" : "update"} user defined field`,
        debtorAsset: `Failed to ${isNew ? "create" : "update"} debtor asset custom field`,
        legal: `Failed to ${isNew ? "create" : "update"} legal custom field`,
        user: `Failed to ${isNew ? "create" : "update"} user defined field`,
      }[forType];
      message.error(errorText);
    }
  };

  const initialValues = {
    ...userDefinedField,
  };

  const modalTitle = {
    account: `${isNew ? "Add" : "Edit"} Account User Defined Field`,
    client: `${isNew ? "Add" : "Edit"} Client User Defined Field`,
    debtor: `${isNew ? "Add" : "Edit"} Debtor User Defined Field`,
    debtorAsset: `${isNew ? "Add" : "Edit"} Debtor Asset Custom Field`,
    legal: `${isNew ? "Add" : "Edit"} Legal Custom Field`,
    user: `${isNew ? "Add" : "Edit"} User Defined Field`,
  }[forType];

  // Create enhanced options that include only multi-select, ensuring no duplicates
  const customFieldTypes = constants?.customFieldTypes || [];
  const multiSelectType = { display: "Multi Select", value: "multi_select" };
  const enhancedCustomFieldTypes = customFieldTypes.some(
    (type) => type.value === multiSelectType.value,
  )
    ? customFieldTypes
    : [...customFieldTypes, multiSelectType];

  const requiresOptions = ["select", "multi_select"].includes(selectedType);

  return (
    <Modal
      title={modalTitle}
      open
      onOk={onOk}
      onCancel={onCancel}
      okButtonProps={{ loading: isLoading }}
    >
      <StyledForm form={form} initialValues={initialValues} layout="vertical">
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="sortSlug"
          label={
            <span>
              Sort Key
              <Tooltip title="Controls the display order of custom fields. Fields are sorted by this value first, then by name. Examples: '01_primary', '02_secondary', 'AAA_top'. Leave empty for default alphabetical sorting.">
                <StyledInfoCircleOutlined />
              </Tooltip>
            </span>
          }
        >
          <Input placeholder="e.g., 01_primary, AAA_top (optional)" />
        </Form.Item>
        <Form.Item name="type" label="Type" rules={[{ required: true }]}>
          <Select
            options={enhancedCustomFieldTypes.map((type) => ({
              label: type.display,
              value: type.value,
            }))}
          />
        </Form.Item>

        {requiresOptions && (
          <Form.Item
            label="Options"
            rules={[{ required: true, message: "Please add at least one option" }]}
          >
            <Form.List name="choices">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <StyledSpace key={field.key} align="baseline">
                      <StyledFormItem
                        {...field}
                        rules={[{ required: true, message: "Option label is required" }]}
                      >
                        <Input placeholder="Option label" />
                      </StyledFormItem>
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </StyledSpace>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add option
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
        )}

        <Form.Item name="isRequired" valuePropName="checked">
          <Checkbox>Required?</Checkbox>
        </Form.Item>
        <Form.Item name="isImportant" valuePropName="checked">
          <Checkbox>Important?</Checkbox>
        </Form.Item>
      </StyledForm>
    </Modal>
  );
}
